/**
 * Team switcher — lets a user with more than one team membership change the
 * active team. Switching activeTeam re-points conversation loading and the SSE
 * subscription (App.tsx re-runs on activeTeam.id), and saves the choice to the
 * server so /me, an admin's "View as", and API-created projects agree with what
 * the user sees. An admin viewing as someone switches locally only. Renders
 * nothing for users in a single team. `data-team-switcher` lets a container
 * that closes on click (the AppHeader drawer) leave this control usable.
 */

import { useUserStore } from '@/stores/userStore'
import { useImpersonationStore } from '@/stores/impersonationStore'
import { meAPI } from '@/services/api'
import { logger } from '../../utils/logger'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

export function TeamSwitcher({ className = 'px-4 pt-3' }: { className?: string }) {
  const { teams, activeTeam, setActiveTeam } = useUserStore()
  const isImpersonating = useImpersonationStore((s) => s.isImpersonating)

  if (!teams || teams.length <= 1 || !activeTeam) return null

  return (
    <div data-team-switcher className={className}>
      <span className="block mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Workspace</span>
      <Select
        value={activeTeam.id}
        onValueChange={(id) => {
          const next = teams.find((t) => t.id === id)
          if (!next) return
          setActiveTeam(next)
          if (!isImpersonating) {
            meAPI.setActiveTeam(next.id).catch((err) => logger.error('Failed to save active team:', err))
          }
        }}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Select team" />
        </SelectTrigger>
        <SelectContent>
          {teams.map((t) => (
            <SelectItem key={t.id} value={t.id} className="text-xs">
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
